import LiveAgentsTableStateColumn from "./live-agents-table-state-column";
import LiveAgentsTableStateDurationColumn from "./live-agents-table-state-duration-column";
import LiveAgentsTableSignedDurationColumn from "./live-agents-table-signed-duration-column";
import useAgents from "hooks-for/agents/use-agents";
import useInteractionsCounts from "hooks-for/interactions/use-interactions-counts";
import { AgentStateType, IInteractionsCount, InteractionState } from "@edgetier/watchtower-types";
import { combineData } from "./live-agents-table.utilities";
import { FunctionComponent, memo } from "react";
import { INTERACTIONS_TABLE_PREFIX } from "constants/interactions";
import { IProps } from "./live-agents-table.types";
import { LIVE_POLL_INTERVAL_MILLISECONDS, LIVE_AGENTS_TABLE_PREFIX } from "constants/pages/live";
import { parseUrlArray } from "@edgetier/utilities";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { useQueryParameters } from "@edgetier/client-components";
import { useRemoveUrlParameter } from "hooks-for/urls/use-remove-url-parameter";
import { useToggleUrlParameter } from "hooks-for/urls/use-toggle-url-parameter";
import {
    Box,
    BoxContent,
    BoxHeader,
    BoxHeading,
    Cell,
    Column,
    Heading,
    NumericValue,
    Table,
    TableColumns,
} from "@edgetier/client-components";
import { useAgentStates } from "hooks-for/agents/use-agent-states";
import { INTERACTIONS_COUNTS_BETWEEN_STATES_ENABLED } from "constants/feature-flags";
import "./live-agents-table.scss";

/**
 * Show all online agents in a table.
 * @returns Table of agents.
 */
const LiveAgentsTable: FunctionComponent<IProps> = () => {
    const removeUrlParameter = useRemoveUrlParameter();
    const toggleUrlParameter = useToggleUrlParameter();

    // Get all agents that aren't offline.
    const { agentId, agentStateType, orderBy, tagId } = useQueryParameters(
        {
            agentId: String,
            agentStateType: (value) => parseUrlArray<AgentStateType>(value),
            orderBy: String,
            tagId: parseUrlArray,
        },
        {
            defaults: { agentStateType: [AgentStateType.Available, AgentStateType.Unavailable] },
            prefix: {
                [LIVE_AGENTS_TABLE_PREFIX]: ["agentStateType", "orderBy"],
                [INTERACTIONS_TABLE_PREFIX]: ["agentId"],
            },
        }
    );

    const { data: agentStates } = useAgentStates({}, { refetchInterval: LIVE_POLL_INTERVAL_MILLISECONDS });

    const queryOptions = { cacheTime: 0, refetchInterval: LIVE_POLL_INTERVAL_MILLISECONDS, staleTime: 0 };
    const { data: agents, isLoading: areAgentsLoading } = useAgents({ agentStateType, orderBy, tagId }, queryOptions);

    // Count interactions being handled.
    const interactionState = [InteractionState.Handling];
    const interactionQueryParameters = useInteractionQueryParameters({ defaults: { interactionState, limit: 500 } });

    const countsQueryParameters = { ...interactionQueryParameters, groupBy: "agent_id" };
    const { data: interactionsCounts, isLoading: areCountsLoading } = useInteractionsCounts<
        IInteractionsCount<string>[]
    >(countsQueryParameters, queryOptions);

    // Count interactions since last agent state change
    const stateUpdateInteractionsParameters = {
        ...interactionQueryParameters,
        agentId: agentId,
        groupBy: "agent_id",
        sinceLastAgentStateUpdate: true,
    };
    const { data: stateChangeInteractionsCounts, isLoading: stateChangeCountsLoading } = useInteractionsCounts<
        IInteractionsCount<string>[]
    >(stateUpdateInteractionsParameters, queryOptions);

    // Join agents with counts of their interactions
    const combinedData = combineData(agents, interactionsCounts, stateChangeInteractionsCounts);

    /**
     * Update the URL when an agent is clicked.
     * @param agent Clicked agent.
     */
    const onClickAgent = ({ agentId }: typeof combinedData[0]) => {
        toggleUrlParameter(`${INTERACTIONS_TABLE_PREFIX}.agentId`, agentId);
    };

    /**
     * When the selected agent disappears from the table the URL needs to be updated.
     */
    const onActiveAgentRemoval = () => {
        removeUrlParameter(`${INTERACTIONS_TABLE_PREFIX}.agentId`);
    };

    return (
        <Box className="live-agents-table">
            <BoxHeader>
                <BoxHeading>
                    <span>Agents </span>
                    {typeof agents !== "undefined" && (
                        <>
                            (<NumericValue value={agents?.length} />)
                        </>
                    )}
                </BoxHeading>
            </BoxHeader>
            <BoxContent horizontalPadding={false}>
                <div className="live-agents-table__container">
                    <Table<typeof combinedData[0]>
                        activeRowKey={agentId ?? null}
                        getRowKey={({ agentId }) => agentId}
                        headerIsSticky
                        isInBox={true}
                        onActiveRowRemoval={onActiveAgentRemoval}
                        onRowClick={onClickAgent}
                        rows={areAgentsLoading || areCountsLoading || stateChangeCountsLoading ? null : combinedData}
                    >
                        <TableColumns>
                            <Column>
                                <Heading>Agent</Heading>
                                <Cell<typeof combinedData[0]>>{({ name }) => name}</Cell>
                            </Column>

                            <LiveAgentsTableStateColumn agentStates={agentStates} />
                            <LiveAgentsTableStateDurationColumn />
                            {INTERACTIONS_COUNTS_BETWEEN_STATES_ENABLED && (
                                <Column>
                                    <Heading>Handled in this state</Heading>
                                    <Cell<typeof combinedData[0]>>
                                        {({ stateChangeInteractions }) => (
                                            <NumericValue value={stateChangeInteractions} />
                                        )}
                                    </Cell>
                                </Column>
                            )}
                            <Column>
                                <Heading sortProperty="handling">Handling</Heading>
                                <Cell<typeof combinedData[0]>>
                                    {({ handling }) => <NumericValue value={handling} />}
                                </Cell>
                            </Column>
                            <LiveAgentsTableSignedDurationColumn />
                        </TableColumns>
                    </Table>
                </div>
            </BoxContent>
        </Box>
    );
};

export default memo(LiveAgentsTable);
